// Converts icon name to kebab-case data-cy string, e.g. edit_profile -> edit-profile-icon
import { forwardRef } from 'react';

import { JoshIconType, getJoshIcon } from './josh-icons/utils';
import { MaterialIconType, getMaterialIcon } from './material-ui-icons/utils';

import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { StrictUnion } from '~/utilities/types';

type BaseProps = TooltipTriggerProps & {
  className?: string;
  /** Size to use, defaults to `md`  */
  size?: 'sm' | 'md' | 'lg';
  /** Style variant to use, defaults to `outlined`  */
  type?: 'filled' | 'outlined';
};

type MaterialIconProps = BaseProps & {
  /** Name of the material-ui icon per https://fonts.google.com/icons?icon.set=Material+Icons&icon.style=Outlined */
  name: MaterialIconType;
};

type JoshIconProps = BaseProps & {
  name: JoshIconType;
};

type Props = StrictUnion<MaterialIconProps | JoshIconProps>;

const sizeClasses = {
  sm: '!icon-sm',
  md: '!icon-md',
  lg: '!icon-lg',
};

const typeClasses = {
  filled: 'material-icon-font icon-filled',
  outlined: 'material-icon-font icon-outlined',
};

export default forwardRef<HTMLSpanElement, Props>(function Icon(props, ref) {
  const JoshIcon = getJoshIcon(props.name, props.type);
  const materialIcon = getMaterialIcon(props.name);
  if (!JoshIcon && !materialIcon) return null;
  return (
    <span
      {...pickTooltipTriggerProps(props)}
      ref={ref}
      aria-hidden="true"
      className={[
        'relative',
        props.className,
        typeClasses[props.type ?? 'outlined'],
        sizeClasses[props.size ?? 'md'],
      ].join(' ')}
      data-cy={getIconDataCy(props.name)}
    >
      {JoshIcon ? <JoshIcon /> : materialIcon}
    </span>
  );
});

// Generates a data-cy string for an icon name in kebab-case.
// Example: edit_profile -> edit-profile-icon
//
// Note: Generating a string for data-cy is strongly discouraged, but I'm
// looking the other way here as icons are not interactive or semantic elements,
// making them hard to target in RTL tests otherwise.
export const getIconDataCy = (name: string) => `${name}-icon`.replace(/_/g, '-');
